import { useState, useEffect, useRef } from "react";
import { ReactComponent as Upload } from "../../assets/icons/upload.svg";
import { ReactComponent as Uploaded } from "../../assets/icons/success-tick.svg";
import { ReactComponent as Delete } from "../../assets/icons/delete.svg";
import Button from "../../components/Button";
import { 
  IconButton,
  Tabs,
  Tab,
  Box,
  Badge,
  Paper,
  Fade,
  Alert,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'
import pocProductsData from '../../data/poc_products.json'
import { fetchAuthSession } from "aws-amplify/auth";
import { environmentConfig } from "../../config/config";

// TabPanel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`search-tabpanel-${index}`}
      aria-labelledby={`search-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in={true} timeout={500}>
          <Box sx={{ pt: 1 }}>
            {children}
          </Box>
        </Fade>
      )}
    </div>
  );
}

export default function ImageSearchPage() {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [pocProducts, setPocProducts] = useState([]);
  const [showPocImages, setShowPocImages] = useState(false);
  const [loadingPocImages, setLoadingPocImages] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [loadedImagesCount, setLoadedImagesCount] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const searchResultsBoxRef = useRef(null);

  const totalPages = Math.ceil(pocProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentProducts = pocProducts.slice(startIndex, startIndex + itemsPerPage);
  
  useEffect(() => {
    setLoadedImagesCount(0);
  }, [currentPage]);

  useEffect(() => {
  if (searchResults && searchResultsBoxRef.current) {
    searchResultsBoxRef.current.scrollIntoView({ behavior: 'smooth' });
  }
}, [searchResults]);

  useEffect(() => {
    if (showPocImages && pocProducts.length === 0) {
      setLoadingPocImages(true);
      setTimeout(() => {
        setPocProducts(pocProductsData);
        setLoadingPocImages(false);
      }, 300);
    }
  }, [showPocImages, pocProducts.length])

  const handleImageChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    
    if (selectedFiles.length === 0) return;
    
    const file = selectedFiles[0];
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select a valid image file");
      return;
    }
    
    setIsUploading(true);
    
    // Simulate upload process
    setTimeout(() => {
      setUploadedImage(file);
      setIsUploading(false);
      setSearchResults(null);
      toast.success("Image uploaded successfully!");
    }, 1000);
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    setSearchResults(null);
    toast.success("Image removed");
  };

  const handleImageSearch = async () => {
    if (!uploadedImage) {
      toast.error("Please upload an image first");
      return;
    }

    setIsSearching(true);
    
    try {
      const arrayBuffer = await uploadedImage.arrayBuffer();
      
      // Get the current session's JWT token
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString();
      
      // Use dynamic image search URL from config based on environment
      const imageSearchUrl = environmentConfig.LAMBDA_URL.IMAGE_SEARCH;
      
      const response = await fetch(imageSearchUrl, {
        method: 'POST',
        body: arrayBuffer,
        headers: {
        'Authorization': `Bearer ${token}`,
      },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const resObj = data?.data?.[0]; 
      const perCropResults = resObj?.perCropResults || [];
      const imgWise = resObj?.wholeImageResults || [];
      
      // Calculate total product matches from all detected objects
      const totalProductMatches = perCropResults.reduce((total, crop) => {
        return total + (crop.results ? crop.results.length : 0);
      }, 0);
      
      if (data.success) {
        setSearchResults({
          perCropResults,
          imgWise,
          executionTimeMs: data.executionTimeMs
        })
        toast.success(
          `Found ${totalProductMatches + imgWise.length} similar images!`
        )
      } else {
        throw new Error(data.error || 'Search failed');
      }
      
    } catch (error) {
      toast.error(`Search failed: ${error.message}`);
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  // Get unique categories from search results
  const availableCategories = searchResults?.perCropResults?.map(crop => crop.detectedObject) || [];

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  // Filter results based on selected category
  const filteredResults = searchResults?.perCropResults?.filter(crop => {
    if (selectedCategory === 'all') return true;
    return crop.detectedObject === selectedCategory;
  }) || [];

  // Calculate filtered count for badge
  const filteredCount = selectedCategory === 'all' 
    ? searchResults?.perCropResults?.reduce((total, crop) => {
        return total + (crop.results ? crop.results.length : 0);
      }, 0) || 0
    : filteredResults.reduce((total, crop) => {
        return total + (crop.results ? crop.results.length : 0);
      }, 0);

  return (
    <div className='p-6'>
      <div className='mx-auto' style={{ width: '75%' }}>
        <Alert severity='info' sx={{ mb: 3 }}>
          <strong>Note: </strong> For single product images, please expect
          similar results for both Product and Image level search results since
          the provided image contains a single object. <strong>Wayfair products are not included in the search results.</strong>
        </Alert>
        <h1 className='text-2xl font-bold mb-5'>Image Search</h1>

        {/* Image Upload Section */}
        <div className="flex flex-col bg-background-white shadow-md rounded-lg px-4 py-8 gap-6 w-full mb-6">
          <div className="text-sm font-medium">Upload Image for Search</div>

          {!uploadedImage ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-background-gray size-12 rounded-full box-center">
                  <Upload />
                </div>
                <div className="flex flex-col gap-0.5">
                  <div className="text-sm font-medium">Upload Your Image</div>
                  <div className="text-sm text-primary/50">Image • Max. 5MB</div>
                </div>
              </div>
              <Button className="relative" small disabled={isUploading}>
                <input
                  type='file'
                  onChange={handleImageChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept="image/*"
                  disabled={isUploading}
                />
                <span className="cursor-pointer">
                  {isUploading ? "Uploading..." : "Upload"}
                </span>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Uploaded file info */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-success/10 size-12 rounded-full box-center">
                    <Uploaded />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <div className="text-sm font-medium">{uploadedImage.name}</div>
                    <div className="text-sm text-primary/50">
                      {dayjs().format("DD MMM, YYYY h:mma")} • {Math.round(uploadedImage.size / 1024)}KB • {uploadedImage.type}
                    </div>
                  </div>
                </div>
                <IconButton onClick={handleRemoveImage}>
                  <Delete />
                </IconButton>
              </div>
              
              {/* Image preview */}
              <div className="mt-4">
                <img 
                  src={URL.createObjectURL(uploadedImage)} 
                  alt="Uploaded preview" 
                  className="max-w-full h-auto max-h-64 rounded-lg shadow-sm"
                />
              </div>
              
              {/* Search button */}
              <div className="flex justify-center mt-6">
                <Button onClick={handleImageSearch} disabled={isSearching}>
                  {isSearching ? "Searching..." : "Search Similar Images"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* POC Images Section */}
        {/* <div className='bg-background-white shadow-md rounded-lg px-4 py-5 w-full mb-5 mt-2'>
          <div className='flex items-center justify-between mb-2'>
            <div className='text-sm font-medium'>
              Search is happening against these products images
              {pocProducts.length > 0 && ` (${pocProducts.length} total)`}
            </div>
            <Button
              small
              onClick={() => {
                setShowPocImages(!showPocImages)
                if (!showPocImages) {
                  setCurrentPage(1)
                }
              }}
              disabled={loadingPocImages}
            >
              {loadingPocImages
                ? 'Loading...'
                : showPocImages
                ? 'Hide Images'
                : 'View Images'}
            </Button>
          </div>

          {showPocImages && (
            <div className='space-y-4'>
              {loadingPocImages ? (
                <div className='text-center py-8'>
                  <div className='animate-pulse'>Loading images...</div>
                </div>
              ) : (
                <div>
                  {totalPages > 1 && (
                    <div className='flex items-center justify-between mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200'>
                      <div className='text-sm text-blue-800 font-medium'>
                        Showing {startIndex + 1}-
                        {Math.min(
                          startIndex + itemsPerPage,
                          pocProducts.length
                        )}{' '}
                        of {pocProducts.length} products
                        {loadedImagesCount < currentProducts.length && (
                          <div className='text-xs text-blue-600 mt-1'>
                            Loading images... {loadedImagesCount}/
                            {currentProducts.length}
                          </div>
                        )}
                      </div>
                      <div className='flex items-center gap-3'>
                        <Button
                          small
                          onClick={() =>
                            setCurrentPage(prev => Math.max(1, prev - 1))
                          }
                          disabled={currentPage === 1}
                          className='!px-4'
                        >
                          ← Previous
                        </Button>
                        <span className='text-sm px-3 py-1 bg-white rounded-lg border text-blue-800 font-medium'>
                          {currentPage} / {totalPages}
                        </span>
                        <Button
                          small
                          onClick={() =>
                            setCurrentPage(prev =>
                              Math.min(totalPages, prev + 1)
                            )
                          }
                          disabled={currentPage === totalPages}
                          className='!px-4'
                        >
                          Next →
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 p-6 bg-gray-50 rounded-lg border'>
                    {currentProducts.map((product, index) => (
                      <div
                        key={product.id || index}
                        className='group relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1'
                      >
                        <div className='aspect-square bg-gray-100 relative overflow-hidden'>
                          <div className='absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse'></div>

                          <img
                            src={product.image_link}
                            alt={product.title || 'Product'}
                            className='w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 relative z-10'
                            loading='lazy'
                            onLoad={e => {
                              // Hide skeleton when image loads
                              const skeleton =
                                e.target.parentElement.querySelector(
                                  '.animate-pulse'
                                )
                              if (skeleton) skeleton.style.display = 'none'
                              setLoadedImagesCount(prev => prev + 1)
                            }}
                            onError={e => {
                              e.target.style.display = 'none'
                              e.target.nextSibling.style.display = 'flex'
                              const skeleton =
                                e.target.parentElement.querySelector(
                                  '.animate-pulse'
                                )
                              if (skeleton) skeleton.style.display = 'none'
                              setLoadedImagesCount(prev => prev + 1)
                            }}
                          />
                          <div className='hidden w-full h-full bg-gray-200 items-center justify-center text-gray-500 text-sm relative z-10'>
                            No Image Available
                          </div>

                          <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300'>
                            <div className='absolute bottom-0 left-0 right-0 p-4 text-white'>
                              <h3 className='font-semibold text-sm mb-1 line-clamp-2'>
                                {product.title}
                              </h3>
                              {product.brand && (
                                <p className='text-gray-300 text-xs mb-1'>
                                  {product.brand}
                                </p>
                              )}
                              {product.price && (
                                <p className='text-green-300 text-sm font-medium'>
                                  ${product.price}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div> */}

        {/* Results section */}
        {uploadedImage && (
          <div className='bg-background-white shadow-md rounded-lg px-4 py-8' ref={searchResultsBoxRef}>
            <div className='mb-4'>
              <div className='text-sm font-medium'>Search Results</div>
              {searchResults && (
                <div className='text-xs text-gray-500 mt-1'>
                  Completed in {(searchResults?.executionTimeMs || 0) / 1000}s
                </div>
              )}
            </div>
            {isSearching ? (
              <div className="text-center text-primary/60 py-8">
                <div className="animate-pulse">Searching for similar images...</div>
              </div>
            ) : searchResults ? (
              <div className="space-y-6">
                {/* Tabs Section */}
                <Paper 
                  elevation={2} 
                  sx={{ 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 10,
                    backgroundColor: 'white'
                  }}
                >
                  <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange}
                    variant="fullWidth"
                    indicatorColor="primary"
                    textColor="primary"
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider'
                    }}
                  >
                    <Tab 
                      iconPosition="start"
                      label={
                        <Badge 
                          badgeContent={filteredCount} 
                          color="primary"
                          sx={{ '& .MuiBadge-badge': { position: 'relative', transform: 'none', ml: 1 } }}
                        >
                          <span>Product Level Matches</span>
                        </Badge>
                      }
                      sx={{ 
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: activeTab === 0 ? 600 : 400
                      }}
                    />
                    <Tab 
                      iconPosition="start"
                      label={
                        <Badge 
                          badgeContent={searchResults.imgWise.length} 
                          color="primary"
                          sx={{ '& .MuiBadge-badge': { position: 'relative', transform: 'none', ml: 1 } }}
                        >
                          <span>Image Level Matches</span>
                        </Badge>
                      }
                      sx={{ 
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: activeTab === 1 ? 600 : 400
                      }}
                    />
                  </Tabs>
                </Paper>

                {/* Category Filters - Only show for Product Level Matches tab */}
                {searchResults && availableCategories.length > 0 && activeTab === 0 && (
                  <Paper elevation={2} sx={{ p: 3 }}>
                    <div className="mb-3">
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedCategory === 'all'}
                              onChange={() => handleCategoryChange('all')}
                              sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                            />
                          }
                          label={<span className="text-sm font-medium">All Categories</span>}
                          sx={{ mr: 3 }}
                        />
                        {availableCategories.map((category) => {
                          const categoryCount = searchResults.perCropResults.find(crop => crop.detectedObject === category)?.results?.length || 0;
                          return (
                            <FormControlLabel
                              key={category}
                              control={
                                <Checkbox
                                  checked={selectedCategory === category}
                                  onChange={() => handleCategoryChange(category)}
                                  sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                />
                              }
                              label={
                                <span className="text-sm capitalize">
                                  {category} ({categoryCount})
                                </span>
                              }
                              sx={{ mr: 3 }}
                            />
                          );
                        })}
                      </FormGroup>
                    </div>
                  </Paper>
                )}

                {/* Tab Panels */}
                <TabPanel value={activeTab} index={0}>
                  {filteredResults.length > 0 ? (
                    <div className="space-y-8">
                      {filteredResults.map((cropResult, cropIndex) => {
                        if (!cropResult.results || cropResult.results.length === 0) return null;
                        
                        return (
                          <div key={cropIndex} className="space-y-4">
                            {/* Detected Object Header */}
                            <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                              <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium capitalize">
                                {cropResult.detectedObject}
                              </div>
                              <span className="text-sm text-gray-600">
                                {cropResult.results.length} match{cropResult.results.length !== 1 ? 'es' : ''}
                              </span>
                            </div>
                            
                            {/* Products Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                              {cropResult.results.map((product, index) => (
                                <div key={`${cropResult.detectedObject}-${product.ItemID || index}`} className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                  <div className="h-48 bg-gray-100 relative">
                                    {product.image_link ? (
                                      <img
                                        src={product.image_link}
                                        alt={product.title || 'Product'}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                    ) : null}
                                    <div className="hidden w-full h-full bg-gray-200 items-center justify-center text-gray-500 text-sm">
                                      No Image
                                    </div>
                                  </div>

                                  <div className="p-4">
                                    <h3 className="font-medium text-sm line-clamp-2 mb-2">
                                      {product.title || 'Untitled Product'}
                                    </h3>
                                    
                                    <div className="space-y-1 text-xs text-gray-600">
                                      {product.brand && (
                                        <div><span className="font-medium">Brand:</span> {product.brand}</div>
                                      )}
                                      {product.price && (
                                        <div><span className="font-medium">Price:</span> ${product.price}</div>
                                      )}
                                      {product.color && (
                                        <div><span className="font-medium">Color:</span> {product.color}</div>
                                      )}
                                      {product.size && (
                                        <div><span className="font-medium">Size:</span> {product.size}</div>
                                      )}
                                      {product.from && (
                                        <div><span className="font-medium">Source:</span> {product.from}</div>
                                      )}
                                      {product.similarityScore && (
                                        <div><span className="font-medium">Similarity:</span> {(product.similarityScore * 100).toFixed(1)}%</div>
                                      )}
                                    </div>

                                    {product.description && (
                                      <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                                        {product.description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      No similar products found
                    </div>
                  )}
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                  {searchResults.imgWise.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {searchResults.imgWise.map((product, index) => (
                        <div key={product.id+index} className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                          <div className="h-48 bg-gray-100 relative">
                            {product.image_link ? (
                              <img
                                src={product.image_link}
                                alt={product.title || 'Product'}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className='hidden w-full h-full bg-gray-200 items-center justify-center text-gray-500 text-sm'>
                              No Image
                            </div>
                          </div>

                          <div className="p-4">
                            <h3 className="font-medium text-sm line-clamp-2 mb-2">
                              {product.title || 'Untitled Product'}
                            </h3>
                            
                            <div className="space-y-1 text-xs text-gray-600">
                              {product.brand && (
                                <div><span className="font-medium">Brand:</span> {product.brand}</div>
                              )}
                              {product.price && (
                                <div><span className="font-medium">Price:</span> ${product.price}</div>
                              )}
                              {product.color && (
                                <div><span className="font-medium">Color:</span> {product.color}</div>
                              )}
                              {product.size && (
                                <div><span className="font-medium">Size:</span> {product.size}</div>
                              )}
                              {product.from && (
                                <div><span className="font-medium">Source:</span> {product.from}</div>
                              )}
                            </div>

                            {product.description && (
                              <p className='text-xs text-gray-500 mt-2 line-clamp-2'>
                                {product.description}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      No similar products found
                    </div>
                  )}
                </TabPanel>

              </div>
            ) : (
              <div className="text-center text-primary/60">
                Click "Search Similar Images" to find similar images
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}